import { ModalService, ConfigurationsService } from '@icc/configurator/shared';
import { IssuesService } from '@icc/shared';
import { IssueLevel } from '@icc/shared/helpers';
import {
    TranslateService,
    APP_CONFIG,
    AppConfigFactory,
    Common,
    ConfiguratorsDataService,
    EventBusService,
} from '@icc/shared';
import { Inject, Injectable } from '@angular/core';
import { CurrentConfiguratorService } from '@icc/configurator/shared';
import { core } from '@icc/shared/helpers';
import { PriceService } from '@icc/price';
import { ColorsPageComponent } from '@icc/construction-elements/configurator';
import { ConstructColorDto as IccConstructColor } from '@icc/construction-elements/types';

@Injectable()
export class ColorsService {
    loadedData = false;
    netColors = [];
    private basicNetColors = [];
    panelColors = [];
    frameColors: IccConstructColor[] = [];
    frameColorsGroups: string[] = [];
    private defaultColors: any = {};

    private configurators = ['mosquito'];
    constructor(
        private modalService: ModalService,
        private translateService: TranslateService,
        private configurationsService: ConfigurationsService<'mosquito' | 'window'>,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private configuratorsDataService: ConfiguratorsDataService,
        private currentConfiguratorService: CurrentConfiguratorService,
        private priceService: PriceService,
        private issuesService: IssuesService,
        private eventBusService: EventBusService
    ) {
        if (this.configuratorsDataService.loaded) {
            this.init();
        }

        this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () => {
            this.init();
        });
    }

    /**
     * Funkcja uruhamiana po zaladowaniu konfiguratora
     */
    private init() {
        this.issuesService.addValidateFunction(this.validate.bind(this));
        this.netColors = core.copy(this.basicNetColors);

        this.panelColors = [
            {
                id: 1,
                color: 'Ffffff',
                name: this.translateService.instant('COLOR|Biały'),
                code: 'white',
                RAL: false
            },
            {
                id: 2,
                color: '654321',
                name: this.translateService.instant('COLOR|Brązowy'),
                code: 'brown',
                RAL: false
            },
        ];
        this.defaultColors = {
            frame: {},
            panel: this.panelColors[0],
            net: this.netColors[0],
        };

        if (this.currentConfiguratorService.conf === 'mosquito' && this.configurationsService.conf.Current.type === 'mosquito') {
            if (
                Common.isUndefined(this.configurationsService.conf.Current.Colors.frame)
                && Common.isUndefined(this.configurationsService.conf.Current.Colors.panel)
                && Common.isUndefined(this.configurationsService.conf.Current.Colors.net)
            ) {
                this.loadDefaultData();
            }

            this.loadColorsBySystem(
                this.configurationsService.conf.Current.System.id,
                this.configurationsService.conf.Current.Type.id
            );
        }

        this.loadedData = true;
    }

    /**
     * Kolory dla danego systemu
     * @param {number} systemId Id wybranego systemu
     */
    loadColorsBySystem(systemId, typeId, netTypeId?) {
        if (Common.isUndefined(this.configurationsService.conf) || Common.isUndefined(systemId)) {
            return;
        }
        this.frameColors =
            core.copy(this.configuratorsDataService.data.windowColorsAll.filter(el => el.systems?.includes(Number(systemId)))) || [];

        this.frameColorsGroups = this.getColorsGroups(this.frameColors, systemId, typeId) || [];

        this.frameColors =
            this.frameColors.filter(color =>
                color.groups?.some(el => this.frameColorsGroups.map(Number).indexOf(+el) > -1)
            ).map(color => ({...color, colorGroups: color.groups})) || [];
        this.frameColors = this.frameColors.sort((a, b) => Number(a.order) - Number(b.order));
        this.loadNetColors(typeId, netTypeId);
        this.setDefaultColors();
    }

    private loadNetColors(typeId: number, selectedNetTypeId?) {
        const conf = this.configurationsService.conf.Current;
        const netTypeId = selectedNetTypeId
            || (conf.type === 'mosquito' ? conf.NetType?.id : null);
        const materialGroupIds = this.configuratorsDataService.data.windowColorGroups
            .filter(group =>
                group.target?.includes('show_mesh_material')
                && Common.isArray(group.mosquitoTypes)
                && group.mosquitoTypes.map(Number).includes(Number(typeId))
                && Common.isArray(group.mosquitoNetTypes)
                && (
                    !netTypeId
                    || group.mosquitoNetTypes.map(Number).includes(Number(netTypeId))
                )
            )
            .map(group => Number(group.id));

        const configuredNetColors = core.copy(this.configuratorsDataService.data.windowColorsAll)
            .filter(color =>
                color.groups?.some(groupId => materialGroupIds.includes(Number(groupId)))
            )
            .map(color => ({ ...color, colorGroups: color.groups }))
            .sort((a, b) => Number(a.order) - Number(b.order));

        this.netColors = [
            ...core.copy(this.basicNetColors),
            ...configuredNetColors.filter(configuredColor =>
                !this.basicNetColors.some(basicColor =>
                    basicColor.code === configuredColor.code
                    || basicColor.color.toLowerCase() === configuredColor.color?.toLowerCase()
                )
            ),
        ];
    }

    private getColorsGroups(allColors: IccConstructColor[], systemId, typeId) {
        // pobierz grupy kolorów z dostępnych kolorów
        const groups = [];
        for (let x = 0; x < allColors.length; ++x) {
            if (Common.isUndefined(allColors[x].groups) || allColors[x].groups === null) {
                continue;
            }
            for (let y = 0; y < allColors[x].groups.length; ++y) {
                if (groups.indexOf(Number(allColors[x].groups[y])) < 0) {
                    groups.push(Number(allColors[x].groups[y]));
                }
            }
        }

        // pobierz grupy kolorów dla danej linii (sprawdź czy grupa kolorów dla linii nie jest pusta)
        const colorGroups = this.configuratorsDataService.data.windowColorGroups
            .filter(el => {
                const val =
                    Common.isArray(el.systems)
                    && Common.isArray(groups)
                    && groups.indexOf(Number(el.id)) > -1
                    && el.systems.indexOf(systemId) > -1
                    && el.target.indexOf('show') > -1
                    && ((Common.isArray(el.mosquitoTypes)
                        && el.mosquitoTypes.length
                        && el.mosquitoTypes.indexOf(typeId) > -1)
                        || (!el.mosquitoTypes && !typeId));
                return val;
            })
            .map(el => el.id);
        return colorGroups;
    }

    /**
     * ustawienie domyślnego koloru
     */
    private setDefaultColors() {
        const conf = this.configurationsService.conf.Current;
        if (conf.type !== 'mosquito') {
            return;
        }
        if (
            Common.isArray(this.frameColors)
            && Common.isDefined(conf.System.id)
        ) {
            const allColors = this.frameColors;
            const whites = allColors.filter(function(el) {
                return el.color.toLowerCase() === 'ffffff';
            });
            let colorF;

            // ustawianie domyślnego koloru
            // biały - pierwszy kolor - pierwszy RAL
            if (
                Common.isObject(conf.Colors.frame)
                && Common.isDefined(conf.Colors.frame.id)
                && allColors.some(
                    color => color.id === conf.Colors.frame.id
                )
            ) {
                colorF = conf.Colors.frame;
            } else if (Common.isDefined(whites) && Common.isDefined(whites[0])) {
                colorF = whites[0];
            } else if (Common.isDefined(this.frameColors[0])) {
                colorF = this.frameColors[0];
            }

            this.setColorSide(this.defaultColors, 'frame', colorF);
        }

        if (Common.isArray(this.netColors)) {
            const mosquitoColors: any = conf.Colors;
            const currentNetColor = this.netColors.find(
                color =>
                    color.id === mosquitoColors.net?.id
                    || (mosquitoColors.net?.code && color.code === mosquitoColors.net.code)
                    || (
                        mosquitoColors.net?.color
                        && color.color?.toLowerCase() === mosquitoColors.net.color.toLowerCase()
                    )
            );
            const netColor = currentNetColor || this.netColors[0];
            this.defaultColors.net = core.copy(netColor);

            if (!currentNetColor) {
                mosquitoColors.net = core.copy(netColor);
                (this.configurationsService.conf.Default.Colors as any).net = core.copy(netColor);
            }
        }

        if (
            this.configurators.indexOf(this.currentConfiguratorService.conf) !== -1
            && Common.isDefined(conf.Colors)
            && Common.isDefined(conf.Colors.frame)
            && Common.isUndefined(
                core.fId(this.frameColors, conf.Colors.frame.id)
            )
        ) {
            this.loadDefaultData();
        }
    }

    /**
     * Ustawianie domyslnego zestawu kolorow
     */
    loadDefaultData() {
        this.configurationsService.conf.Current.Colors = core.copy(this.defaultColors);
        this.configurationsService.conf.Default.Colors = core.copy(this.defaultColors);

        this.priceService.count();
    }

    /**
     * Pzzypisanie skladowych koloru w wybranym elemencie
     * @param {object} object Kolor elementu
     * @param {object} color  Nowy kolor
     */
    setColorSide(object, element, color) {
        if (Common.isDefined(color)) {
            object[element] = core.copy(color);

            this.priceService.count();
        }
    }

    /**
     * Wczytanie kolorow z konfiguracji
     * @param {object} color   Wybrany kolor
     * @param {string} group   Typ koloru
     * @param {string} element Element kolorowany
     * @param {object} object  Miejsce wystpienia elementu
     */
    setColor(color, element, object?) {
        let objectd;

        if (!Common.isDefined(object)) {
            object = this.configurationsService.conf.Current.Colors;
            objectd = this.configurationsService.conf.Default.Colors;
            this.setColorSide(objectd, element, color);
        }

        this.setColorSide(object, element, color);
    }

    /**
     * Otwiera okno modalne z wyborem kolorow
     * @param  {string} type Typ koloru
     */
    openModalColor(type: 'panel' | 'net' | 'frame', sash?) {
        let selectedColor = this.configurationsService.conf.Current.Colors[type];
        if (sash) {
            selectedColor = sash[type];
        }
        const modalInstance = this.modalService.open({
            templateUrl: 'modalMosquitoColor.html',
            controller: 'ModalMosquitoColorCtrl as gColor',
            pageComponent: ColorsPageComponent,
            resolve: {
                colors: () => {
                    if (type === 'net') {
                        return this.netColors;
                    } else if (type === 'panel') {
                        return this.panelColors;
                    } else {
                        return this.frameColors;
                    }
                },
                selColor: () => {
                    return selectedColor;
                },
                selectedColor: () => selectedColor,
                type: () => {
                    return type;
                },
                colorGroups: () => {
                    if (type === 'net') {
                        return [];
                    } else if (type === 'panel') {
                        return [];
                    } else {
                        return this.configuratorsDataService.data.windowColorGroups.filter(
                            el =>
                                this.frameColorsGroups.indexOf(el.id) > -1
                                && el.target.indexOf('show') > -1
                        );
                    }
                }
            },
        });

        modalInstance.result.then(selectedColor => {
            if (selectedColor) {
                this.setColor(selectedColor.color, type, sash);
            }
        });
    }

    /**
     * Sprawdza, czy został wybrany jakiś kolor
     */
    validate() {
        const conf = this.configurationsService.conf.Current;
        if (conf.type !== 'mosquito') {
            return;
        }
        if (this.configurators.indexOf(this.currentConfiguratorService.conf) >= 0) {
            if (!conf.Colors.net || conf.Colors.net.name === null) {
                this.issuesService.simpleRegister(
                    'no-mosquito-colors',
                    'Kolor siatki nie został określony.',
                    this.translateService.instant('MOSQUITO|Kolor siatki nie został określony.'),
                    conf,
                    {
                        logLevel: IssueLevel.NONE
                    }
                );
                return false;
            }
        }

        this.issuesService.unregister(
            'no-mosquito-colors',
            conf
        );
        return true;
    }
}
